import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardHalfDonutChart from '@components/pages/Dashboard/MetricCharts/DashboardHalfDonutChart.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import type { UseProductionByDepartmentResult } from '@hooks/dashboard/useProductionByDepartment'
import {
  buildComparisonFooter,
  formatChartCurrencyKUnits,
  formatCurrency,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  FinanceHalfDonutLegend,
  FinanceMetricTileItem,
} from '../../FinanceMetricTiles.component'
import { ComparisonMetricFooter } from '../shared/dashboardFinanceMetricFooter'

type DentistHygienistProductionCardProps = {
  titleHref: string
  data: Pick<
    UseProductionByDepartmentResult,
    | 'dentistMetric'
    | 'hygienistMetric'
    | 'donutSlices'
    | 'loading'
    | 'hasPayload'
    | 'hasDentistHygienistData'
  >
}

function comparisonMetricFooter(
  metric:
    | { current: number; previous: number; percentChange: number }
    | undefined,
): React.ReactNode {
  if (!metric) return undefined
  return (
    <ComparisonMetricFooter
      {...buildComparisonFooter(metric, { isCurrency: true })}
    />
  )
}

export function DentistHygienistProductionCard({
  titleHref,
  data: dept,
}: DentistHygienistProductionCardProps): React.ReactElement {
  const {
    dentistMetric,
    hygienistMetric,
    donutSlices,
    loading,
    hasPayload,
    hasDentistHygienistData,
  } = dept
  const dentistCurrent = dentistMetric?.current ?? 0
  const hygienistCurrent = hygienistMetric?.current ?? 0
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasDentistHygienistData,
  )
  const pending = isDashboardSectionPending(loading, hasPayload)

  return (
    <DashboardSectionCard
      title="Dentist vs hygienist production"
      titleHref={titleHref}
      className="dashboard-finance-half-donut-card"
      loading={pending}
      skeleton={{ metrics: 2, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-finance-half-donut-content">
        <div className="dashboard-finance-half-donut-metrics">
          <FinanceMetricTileItem
            leftLabel="Dentist production"
            value={
              dentistMetric
                ? formatCurrency(dentistMetric.current, { compact: true })
                : HYGIENE_PLACEHOLDER
            }
            footer={comparisonMetricFooter(dentistMetric)}
          />
          <FinanceMetricTileItem
            leftLabel="Hygienist production"
            value={
              hygienistMetric
                ? formatCurrency(hygienistMetric.current, { compact: true })
                : HYGIENE_PLACEHOLDER
            }
            footer={comparisonMetricFooter(hygienistMetric)}
          />
        </div>
        {isEmpty ? (
          <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
        ) : (
          <>
            <div className="dashboard-finance-half-donut-chart-area">
              <DashboardHalfDonutChart
                title=""
                primaryValue={dentistCurrent / 1000}
                secondaryValue={hygienistCurrent / 1000}
                valueFormatter={formatChartCurrencyKUnits}
                slices={donutSlices.map((s) => ({
                  ...s,
                  value: s.value / 1000,
                }))}
                hideCenterMetric
                className="dashboard-finance-half-donut"
              />
            </div>
            <FinanceHalfDonutLegend slices={donutSlices} />
          </>
        )}
      </div>
    </DashboardSectionCard>
  )
}
